import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, Settings, Search, FileText, User } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const QuotesView = ({ 
  quotes, 
  searchTerm, 
  setSearchTerm, 
  setCurrentView, 
  setEditingQuote, 
  setViewingQuote,
  handleDeleteQuote, 
  isDarkMode, 
  handleThemeToggle,
  currentUser
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [userDisplayName, setUserDisplayName] = useState('');
  
  // Detect mobile screen size for placeholder text
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load user profile to get business name or email
  const loadUserDisplayName = useCallback(async () => {
    if (!currentUser) {
      setUserDisplayName('');
      return;
    }

    try {
      // Try localStorage first (faster, works offline)
      const localProfile = localStorage.getItem(`userProfile_${currentUser.uid}`);
      if (localProfile) {
        const parsed = JSON.parse(localProfile);
        if (parsed.businessName && parsed.businessName.trim()) {
          setUserDisplayName(parsed.businessName.trim());
          return;
        }
      }

      // Try Firestore
      const profileRef = doc(db, 'users', currentUser.uid, 'profile', 'data');
      const profileDoc = await getDoc(profileRef);
      
      if (profileDoc.exists()) {
        const data = profileDoc.data();
        if (data.businessName && data.businessName.trim()) {
          setUserDisplayName(data.businessName.trim());
          // Update localStorage
          localStorage.setItem(`userProfile_${currentUser.uid}`, JSON.stringify({
            businessName: data.businessName,
            phoneNumber: data.phoneNumber || '',
            address: data.address || ''
          }));
          return;
        }
      }

      // Fallback to email
      setUserDisplayName(currentUser.email || 'Anonymous User');
    } catch (error) {
      console.error('Error loading user display name:', error);
      // Fallback to email on error
      setUserDisplayName(currentUser.email || 'Anonymous User');
    }
  }, [currentUser]);

  useEffect(() => {
    loadUserDisplayName();
  }, [loadUserDisplayName]);

  // Listen for profile updates
  useEffect(() => {
    const handleProfileUpdate = (event) => {
      const { businessName, email } = event.detail;
      if (businessName && businessName.trim()) {
        setUserDisplayName(businessName.trim());
      } else {
        // If business name was cleared, use email or reload
        if (email && email.trim()) {
          setUserDisplayName(email.trim());
        } else {
          loadUserDisplayName();
        }
      }
    };

    window.addEventListener('userProfileUpdated', handleProfileUpdate);
    return () => {
      window.removeEventListener('userProfileUpdated', handleProfileUpdate);
    };
  }, [loadUserDisplayName]);
  
  const filteredQuotes = quotes
    .filter(quote => 
      quote.customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.customer.address.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const aTime = new Date(a.updatedAt || a.createdAt || 0);
      const bTime = new Date(b.updatedAt || b.createdAt || 0);
      return bTime - aTime; // Newest first
    });

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) {
      // If no date provided, use today's date
      return new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
    
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      // If invalid date, use today's date
      return new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-primary mobile-app">
      <div className="header shadow-lg border-b border-primary sticky top-0 z-10">
        {/* Mobile Header Layout */}
        <div className="flex items-center justify-between w-full gap-2">
          {/* Left: Title */}
          <h1 className="text-lg md:text-2xl font-bold text-primary m-0 flex-shrink-0">SQUEEKIT</h1>
          
          {/* Center: Search Bar - Inline on mobile, expanded on desktop */}
          <div className="flex flex-1 mx-2 sm:mx-4">
            <div className="relative w-full max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-tertiary w-4 h-4 pointer-events-none" />
              <input
                type="text"
                placeholder={isMobile ? "Search" : "Search quotes..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input w-full focusable"
              />
            </div>
          </div>
          
          {/* Right: New Quote Button + Hamburger Menu */}
          <div className="flex items-center gap-2 flex-shrink-0 relative header-actions-with-menu" ref={menuRef}>
            {/* New Quote Button */}
            <button
              onClick={() => {
                setCurrentView('newQuote');
                setIsMenuOpen(false);
              }}
              className="btn btn-primary"
            >
              <Plus className="w-4 h-4 sm:mr-2 inline" />
              <span className="hidden sm:inline">New Quote</span>
            </button>
            
            {/* Hamburger Menu */}
            <div className={`hamburger-menu ${isMenuOpen ? 'open' : ''}`}>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="hamburger-button touchable"
                aria-label="Menu"
              >
                <div className="hamburger-line"></div>
                <div className="hamburger-line"></div>
                <div className="hamburger-line"></div>
              </button>
            </div>
            
            {/* Dropdown - spans full width of container for symmetry with New Quote button */}
            <div className={`hamburger-dropdown ${isMenuOpen ? '' : 'hidden'}`}>
              <button
                onClick={() => {
                  setCurrentView('settings');
                  setIsMenuOpen(false);
                }}
                className="hamburger-item hamburger-item-grey touchable"
              >
                <Settings className="w-4 h-4" />
                Settings
              </button>
              <button
                onClick={() => {
                  setCurrentView('userProfile');
                  setIsMenuOpen(false);
                }}
                className="hamburger-item hamburger-item-grey touchable"
              >
                <User className="w-4 h-4" />
                <span className="text-sm">
                  {userDisplayName || currentUser?.email || 'Anonymous User'}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="main-content">
        {quotes.length === 0 ? (
          <div className="card text-center">
            <FileText className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-4 text-tertiary" />
            <h2 className="text-xl md:text-2xl font-bold text-primary mb-2">
              No Quotes Yet
            </h2>
            <p className="text-tertiary mb-6 text-sm md:text-base">
              Create your first quote to get started
            </p>
            <button
              onClick={() => setCurrentView('newQuote')}
              className="btn btn-primary"
            >
              <Plus className="w-5 h-5 mr-2 inline" />
              Create First Quote
            </button>
          </div>
        ) : (
          <div className="quote-grid">
            {filteredQuotes.map(quote => {
              const lineItems = quote.structures?.length > 0
                ? quote.structures.flatMap(s => s.lineItems || [])
                : (quote.lineItems || []);
              const totalWindowCount = lineItems.reduce((sum, item) => sum + (item.totalCount || 0), 0);
              const structureCount = quote.structures?.length ?? 0;

              return (
                <div 
                  key={quote.id} 
                  className="quote-card"
                  onClick={() => {
                    setViewingQuote(quote);
                    setCurrentView('viewingQuote');
                  }}
                >
                  {/* Desktop Layout: Left info | Right price */}
                  <div className="quote-info">
                    <div className="quote-name">{quote.customer.name}</div>
                    <div className="quote-address">{quote.customer.address}</div>
                    {structureCount > 1 && (
                      <div className="text-xs text-gray-500 mt-0.5">{structureCount} buildings</div>
                    )}
                    <div className="quote-date">{formatDate(quote.updatedAt)}</div>
                  </div>
                  
                  <div className="quote-right">
                    <div className="quote-window-count">Total {totalWindowCount} window{totalWindowCount !== 1 ? 's' : ''}</div>
                    <div className="quote-price">${quote.totals.total.toFixed(2)}</div>
                  </div>
                  
                  {/* Mobile Layout: Row-based structure */}
                  <div className="quote-row-mobile">
                    <div className="quote-row">
                      <div className="quote-name">{quote.customer.name}</div>
                      <div className="quote-price">${quote.totals.total.toFixed(2)}</div>
                    </div>
                    
                    <div className="quote-row">
                      <div className="quote-address">{quote.customer.address}</div>
                      <div className="quote-window-count">Total {totalWindowCount} window{totalWindowCount !== 1 ? 's' : ''}</div>
                    </div>
                    
                    <div className="quote-row">
                      <div className="quote-date">{formatDate(quote.updatedAt)}</div>
                      <div></div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuotesView;
